import { useCallback, useEffect } from 'react';

import { ErrorCodes } from '@aline/common';
import type { UUID } from '@aline/common';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useSnackbar } from 'notistack';
import { Apollo } from 'src/gql/generated';
import { getErrorCode } from 'src/store/errors';

/**
 * Fallback poll cadence for the status query. The `bulkExportUpdated`
 * subscription is the primary channel; this only exists to recover from a
 * dropped/missed event (e.g. a completion published while the socket was
 * reconnecting) so the snackbar can't get stuck on Processing forever. Kept
 * looser than the server's 5s publish throttle since it's a safety net, not
 * the live feed, and stops entirely once the export reaches a terminal state.
 */
const STATUS_POLL_INTERVAL_MS = 10_000;

export function useBulkExport() {
  const { enqueueSnackbar } = useSnackbar();
  const [requestMutation, { loading }] = Apollo.useRequestBulkExportMutation();
  const [cancelMutation, { loading: cancelLoading }] = Apollo.useCancelBulkExportMutation();

  const requestBulkExport = useCallback(
    async (input: Apollo.RequestBulkExportInput) => {
      try {
        const { data } = await requestMutation({ variables: { input } });
        // No success snackbar here — the BulkExportTaskPanel in the background-
        // task tray is the live indicator. A second "Bulk export started" toast
        // would just clutter the bottom-right corner.
        return data?.requestBulkExport ?? null;
      } catch (error) {
        enqueueSnackbar(error instanceof Error ? error.message : 'Failed to start bulk export', {
          variant: 'error',
        });
        return null;
      }
    },
    [requestMutation, enqueueSnackbar]
  );

  const cancelBulkExport = useCallback(
    async (exportId: UUID): Promise<boolean> => {
      try {
        await cancelMutation({ variables: { id: exportId } });
        return true;
      } catch (error) {
        enqueueSnackbar(error instanceof Error ? error.message : 'Failed to cancel export', {
          variant: 'error',
        });
        return false;
      }
    },
    [cancelMutation, enqueueSnackbar]
  );

  return { cancelBulkExport, cancelLoading, loading, requestBulkExport };
}

/**
 * Subscribes to live progress updates for a bulk export and falls back to a
 * one-shot query for the initial state. Returns the latest BulkExport entity,
 * suitable for driving a progress bar / status banner.
 *
 * We read from the subscription's data directly (in addition to relying on
 * Apollo's cache normalization) so a COMPLETED event that lands before the
 * initial query response still flips the UI to terminal state. Without this
 * the progress bar could stall on `Pending` until the cache catches up.
 */
export function useBulkExportStatus(exportId: UUID | null) {
  // Empty-string id sidesteps the generated Apollo signature which requires
  // `variables` even when `skip: true`. The skip guard prevents the request.
  const variables = { id: exportId ?? '' };

  const subscription = Apollo.useBulkExportUpdatedSubscription({ skip: !exportId, variables });
  const queryResult = Apollo.useBulkExportQuery({ skip: !exportId, variables });

  /**
   * Merge the two channels by whichever is furthest along rather than always
   * preferring the subscription. A missed completion event leaves the
   * subscription stuck on PROCESSING; preferring it unconditionally would hide
   * the poll's COMPLETED/FAILED result — the exact failure the poll exists to
   * cover. A terminal status from either channel wins; among in-flight states
   * the one further through prepare-then-zip wins; the pre-first-response race
   * still shows a COMPLETED subscription immediately.
   */
  const bulkExport = pickFurthestBulkExport(
    subscription.data?.bulkExportUpdated ?? null,
    queryResult.data?.bulkExport ?? null
  );

  const isTerminal = bulkExport ? getIsTerminalBulkExportStatus(bulkExport.status) : false;

  /**
   * Poll the query as a fallback while the export is in flight, and stop the
   * moment it reaches a terminal state (or there's no export to watch) so we
   * don't keep hitting the server after there's nothing left to learn.
   */
  const { startPolling, stopPolling } = queryResult;
  useEffect(() => {
    if (!exportId || isTerminal) {
      stopPolling();
      return;
    }
    startPolling(STATUS_POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [exportId, isTerminal, startPolling, stopPolling]);

  // Only a definitive NOT_FOUND means the export is gone; a transient network or
  // auth error is not "missing", so a restored panel is never dropped over a blip.
  const notFound = getIsBulkExportMissing(queryResult.error);

  return { bulkExport, loading: queryResult.loading, notFound };
}

function getIsBulkExportMissing(error: unknown): boolean {
  if (!CombinedGraphQLErrors.is(error)) return false;
  return error.errors.some(graphQLError => getErrorCode(graphQLError) === ErrorCodes.NOT_FOUND);
}

function getIsTerminalBulkExportStatus(status: Apollo.BulkExportStatus): boolean {
  return status === Apollo.BulkExportStatus.Completed || status === Apollo.BulkExportStatus.Failed;
}

/**
 * Pick the record that reflects the most progress across the two status
 * channels: a terminal status always beats a non-terminal one, and among
 * in-flight records the one further along wins.
 */
function pickFurthestBulkExport<
  T extends { packagedCount: number; processedCount: number; status: Apollo.BulkExportStatus },
>(a: T | null, b: T | null): T | null {
  if (!a) return b;
  if (!b) return a;
  const aTerminal = getIsTerminalBulkExportStatus(a.status);
  const bTerminal = getIsTerminalBulkExportStatus(b.status);
  if (aTerminal !== bTerminal) return aTerminal ? a : b;
  return getProgressRank(b) > getProgressRank(a) ? b : a;
}

/**
 * How far an in-flight export has got. `processedCount` covers the prepare
 * step and `packagedCount` the zip step, which only starts once
 * `processedCount` reaches the total, so their sum orders both steps. Ranking
 * on `processedCount` alone ties for the whole zip step, and a stale
 * subscription snapshot would then hide the poll's newer zip progress.
 */
function getProgressRank(bulkExport: { packagedCount: number; processedCount: number }): number {
  return bulkExport.processedCount + bulkExport.packagedCount;
}
